import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { noop } from 'lodash';
import type { ReactElement } from 'react';
import type { TransProps } from 'react-i18next';

export interface ConfirmationDialogData {
  isOpen: boolean;
  title: string;
  message?: string;
  messageData?: {
    // i18n support
    i18n?: TransProps<string, string | readonly string[], string | undefined, string>;
    /**
     * Components to substitute into <tag>...</tag> placeholders in the i18n string.
     * - string value → rendered as a `<Link href={value}>` (existing behavior)
     * - ReactElement → passed through directly (e.g., `<Typography fontWeight="bold" />`)
     */
    data: Record<string, string | ReactElement | undefined>;
  };
  confirmBtnText?: string;
  cancelBtnText?: string;
  disableBackdropClose?: boolean;
  onConfirm: (checkboxChecked?: boolean) => void | Promise<void>;
  onClose?: () => void | Promise<void>;
  width?: number;
  /** Optional "Don't show again" checkbox; dialog owns UI state and passes value to onConfirm */
  checkboxLabel?: string;
  isCheckboxChecked?: boolean;
}

interface ConfirmationDialogState {
  confirmationDialogData: ConfirmationDialogData;
  openConfirmationDialog: (data: Omit<ConfirmationDialogData, 'isOpen'>) => void;
  closeConfirmationDialog: () => void;
}

const initialConfirmationDialogData: ConfirmationDialogData = {
  isOpen: false,
  title: '',
  onConfirm: noop,
};

export const useConfirmationDialogStore = create<ConfirmationDialogState>()(
  devtools(
    (set) => ({
      // Initial state
      confirmationDialogData: initialConfirmationDialogData,
      openConfirmationDialog: (data: Omit<ConfirmationDialogData, 'isOpen'>) =>
        set(() => ({ confirmationDialogData: { ...data, isOpen: true } })),
      closeConfirmationDialog: () =>
        set(() => ({
          confirmationDialogData: initialConfirmationDialogData,
        })),
    }),
    {
      name: 'Confirmation Dialog Store',
      enabled: import.meta.env.DEV,
    },
  ),
);
